import { Container } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Divider, Grid } from "@mui/material";
import ExcelLogo from "assets/img/excel-logo.png";
import axios from "axios";
import {
  ArrayChip,
  AutoCompleteWithSearch,
  ButtonGroupTop,
  CustomTable,
  ImportDocumentModal,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SearchTextInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl, hardTypeWeb } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors, wordCapitalize } from "utils";

function Pencipta({ userLogin }) {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const rolePublisher = userLogin?.role?.type === "publisher";
  const isCaris = hardTypeWeb === "caris";
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");
  const paramsOriginalPublisher = urlParams?.get("original_publisher_id");

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loadingButton, setLoadingButton] = useState({
    invitation: false,
    excel: false,
  });
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    original_publisher_id: Number(paramsOriginalPublisher) || 0,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedComposers, setSelectedComposers] = useState([]);
  const allComposerSelected = dataTable?.every(option =>
    selectedComposers?.some(
      selected => selected?.composer_id === option?.composer_id
    )
  );
  const [searchOriginalPublisher, setSearchOriginalPublisher] = useState("");
  const [optionOriginalPublisher, setOptionOriginalPublisher] = useState([]);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleSelectComposer = (event, option) => {
    const { checked } = event?.target || false;
    setSelectedComposers(prev =>
      checked
        ? [...prev, option]
        : prev.filter(item => item.composer_id !== option.composer_id)
    );
  };
  const handleSelectAllComposer = event => {
    const { checked } = event?.target || false;
    setSelectedComposers(checked ? dataTable : []);
  };
  const handleConfirmInvitation = () =>
    ModalWarning(
      `Are you sure want to send invitation to ${
        !selectedComposers?.length
          ? "all user"
          : selectedComposers?.length === 1
          ? "selected user"
          : `${selectedComposers?.length} users`
      } ?`,
      "Send Invitation"
    ).then(res => res?.isConfirmed && handleSendInvitation());
  const handleLoadingButon = (value, key) => {
    setLoadingButton(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/composers`, {
        headers,
        params: queryParams,
      });

      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const getOptionOriginalPublisher = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(
        `${hardBaseUrl}/publishers/original-publishers`,
        {
          headers,
          params: {
            page: 1,
            size: 50,
            ...(searchOriginalPublisher && { search: searchOriginalPublisher }),
          },
        }
      );
      setOptionOriginalPublisher(res?.data?.data || []);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const debounceOptionOriginalPublisher = useCallback(
    debounce(() => {
      getOptionOriginalPublisher();
    }, 500),
    [searchOriginalPublisher]
  );
  const handleSendInvitation = async () => {
    const composer_ids =
      selectedComposers?.length > 0
        ? selectedComposers.map(item => item?.composer_id)
        : [];
    try {
      handleLoadingButon(true, "invitation");
      const res = await axios.post(
        `${hardBaseUrl}/publisher/composer/invitation`,
        { composer_ids },
        { headers }
      );
      ModalSuccess(res.data.data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      handleLoadingButon(false, "invitation");
    }
  };
  const handleUploadDocument = async documentFiles => {
    const formData = new FormData();
    formData.append("file", documentFiles[0]);
    try {
      handleLoadingButon(true, "excel");
      await axios.post(`${hardBaseUrl}/publisher/composer/import`, formData, {
        headers,
      });
      ModalSuccess(
        "You can check the progress of the upload process in the Upload Monitoring Menu",
        "Data is being processed"
      ).then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setModalVisible(false);
      handleLoadingButon(false, "excel");
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);
  useEffect(() => {
    const isSearching = Boolean(searchOriginalPublisher);
    if (rolePublisher) {
      if (isSearching) {
        debounceOptionOriginalPublisher();
        return () => {
          debounceOptionOriginalPublisher.cancel();
        };
      } else {
        getOptionOriginalPublisher();
      }
    }
  }, [searchOriginalPublisher, debounceOptionOriginalPublisher]);

  return (
    <Page className={classes.root} title="Composer">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <Grid container columnSpacing={2}>
              <Grid item>
                <SearchTextInput
                  placeholder="Search"
                  value={queryParams?.search}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "search")
                  }
                  disabled={loadingPage}
                />
              </Grid>
              {rolePublisher && (
                <Grid item>
                  <AutoCompleteWithSearch
                    label="Original Publisher"
                    options={optionOriginalPublisher}
                    optionLabel="name"
                    loading={loadingFilter}
                    inputValue={searchOriginalPublisher}
                    onInputChange={setSearchOriginalPublisher}
                    value={
                      optionOriginalPublisher?.find(
                        option =>
                          option?.original_publisher_id ===
                          queryParams?.original_publisher_id
                      ) || null
                    }
                    onChange={item =>
                      handleChangeQueryParams(
                        item?.original_publisher_id,
                        "original_publisher_id"
                      )
                    }
                    disabled={loadingPage || loadingFilter}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1}>
              <Grid item>
                <PrimaryButton
                  label="Upload Excel"
                  onClick={() => setModalVisible(true)}
                  disabled={loadingPage || loadingButton?.excel}
                  loading={loadingButton?.excel}
                  startIcon={<img src={ExcelLogo} alt="logo" />}
                  size="large"
                  color="#34774C"
                />
              </Grid>
              {isCaris && (
                <Grid item>
                  <PrimaryButton
                    label="Send Invitation"
                    onClick={handleConfirmInvitation}
                    disabled={loadingPage || loadingButton?.invitation}
                    loading={loadingButton?.invitation}
                    startIcon={<AddIcon />}
                    size="large"
                  />
                </Grid>
              )}
              <Grid item>
                <PrimaryButton
                  label="Add Composer/Author"
                  onClick={() =>
                    history.push("/admin/parameter/pencipta/tambah")
                  }
                  startIcon={<AddIcon />}
                  size="large"
                  disabled={loadingPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable({ isCaris })}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          sortValue={queryParams?.sort}
          onRequestSort={sortBy =>
            handleChangeQueryParams(
              `${sortBy} ${
                queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
              }`,
              "sort"
            )
          }
          isHaveAction
          handleEdit={item =>
            history.push(`/admin/parameter/pencipta/edit/${item.composer_id}`)
          }
          isHaveCheckbox={isCaris}
          checkboxHeaderChecked={allComposerSelected}
          handleCheckboxHeader={handleSelectAllComposer}
          checked={item =>
            selectedComposers?.some(
              selected => selected?.composer_id === item?.composer_id
            )
          }
          handleCheckbox={(e, item) => handleSelectComposer(e, item)}
        />
      </Container>
      {modalVisible && (
        <ImportDocumentModal
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          onSubmit={handleUploadDocument}
          fileType=".xlsx, .xls"
          loadingButton={loadingButton?.excel}
          downloadFile
          downloadComposerTemplate
        />
      )}
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Parameter",
    link: "/admin/parameter/pencipta",
  },
  {
    label: "Composer",
    active: true,
  },
];
const columnTable = ({ isCaris }) => [
  {
    name: "composer_id",
    title: "ID Composer/Author",
    sort_key: "composer_id",
    useSort: true,
  },
  {
    name: "sure_name",
    title: "Composer/Author Name",
    sort_key: "sure_name",
    useSort: true,
  },
  {
    name: "alias_names",
    title: "Alias Name",
    renderText: item => <ArrayChip list={item} />,
  },
  {
    name: "composer_tax",
    title: "Tax",
  },
  {
    name: "phone_number",
    title: "Phone",
    sort_key: "phone_number",
    useSort: true,
  },
  {
    name: "email",
    title: "Email",
    sort_key: "email",
    useSort: true,
  },
  ...(isCaris
    ? [
        {
          name: "last_invitation",
          title: "Last Sent",
          sort_key: "last_invitation",
          useSort: true,
          renderText: item => formatDate(item),
        },
        {
          name: "status",
          title: "Status",
          renderText: status => (
            <StatusChip
              type={
                status === "active"
                  ? "success"
                  : status === "pending"
                  ? "warning"
                  : ""
              }
              label={wordCapitalize(status)}
            />
          ),
        },
      ]
    : []),
];

export default Pencipta;
